/**
 * AsciiBuffer - Intensity-based character buffer for ASCII rendering
 * Inspired by play.ertdfgcvb.xyz's Dyna example
 */

interface Cell {
  value: number
  char?: string
}

export class AsciiBuffer {
  public cells: Cell[]
  public cols: number
  public rows: number
  private density = ' .:░▒▓█~#+-'.split('')

  constructor(cols: number, rows: number) {
    this.cols = cols
    this.rows = rows
    this.cells = Array(cols * rows)
      .fill(0)
      .map(() => ({ value: 0 }))
  }

  /**
   * Set intensity at grid position (0-1 range)
   */
  set(x: number, y: number, intensity: number): void {
    const col = Math.floor(x)
    const row = Math.floor(y)

    if (col < 0 || col >= this.cols || row < 0 || row >= this.rows) return

    const idx = col + row * this.cols
    // Take maximum intensity (additive)
    this.cells[idx].value = Math.max(this.cells[idx].value, intensity)
  }

  /**
   * Draw line using Bresenham's algorithm
   */
  drawLine(x0: number, y0: number, x1: number, y1: number, intensity = 1.0): void {
    x0 = Math.floor(x0)
    y0 = Math.floor(y0)
    x1 = Math.floor(x1)
    y1 = Math.floor(y1)

    const dx = Math.abs(x1 - x0)
    const dy = Math.abs(y1 - y0)
    const sx = x0 < x1 ? 1 : -1
    const sy = y0 < y1 ? 1 : -1
    let err = dx - dy

    while (true) {
      this.set(x0, y0, intensity)

      if (x0 === x1 && y0 === y1) break

      const e2 = 2 * err
      if (e2 > -dy) {
        err -= dy
        x0 += sx
      }
      if (e2 < dx) {
        err += dx
        y0 += sy
      }
    }
  }

  /**
   * Draw point with falloff radius
   */
  drawPoint(x: number, y: number, radius: number, intensity = 1.0): void {
    const cx = Math.floor(x)
    const cy = Math.floor(y)
    const r = Math.ceil(radius)

    // Draw circle with smooth falloff
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        const dist = Math.sqrt(dx * dx + dy * dy)
        if (dist <= radius) {
          const falloff = 1 - dist / radius
          this.set(cx + dx, cy + dy, intensity * falloff)
        }
      }
    }
  }

  /**
   * Draw text at grid position
   */
  drawText(x: number, y: number, text: string, intensity = 1.0): void {
    const startCol = Math.floor(x)
    const row = Math.floor(y)

    if (row < 0 || row >= this.rows) return

    for (let i = 0; i < text.length; i++) {
      const col = startCol + i
      if (col < 0 || col >= this.cols) continue

      const idx = col + row * this.cols
      this.cells[idx].value = Math.max(this.cells[idx].value, intensity)
      this.cells[idx].char = text[i]
    }
  }

  /**
   * Multiplicative decay for Dyna-style trails
   */
  decay(factor = 0.98): void {
    for (let i = 0; i < this.cells.length; i++) {
      this.cells[i].value *= factor
      // Clear custom characters when they fade below threshold
      if (this.cells[i].value < 0.05 && this.cells[i].char) {
        delete this.cells[i].char
      }
    }
  }

  /**
   * Clear the buffer
   */
  clear(): void {
    for (let i = 0; i < this.cells.length; i++) {
      this.cells[i].value = 0
      delete this.cells[i].char
    }
  }

  /**
   * Smoothstep function for smooth transitions
   */
  private smoothstep(edge0: number, edge1: number, x: number): number {
    const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)))
    return t * t * (3 - 2 * t)
  }

  /**
   * Convert intensity to character
   */
  getChar(intensity: number): string {
    const smooth = this.smoothstep(0, 0.9, intensity)
    const idx = Math.floor(smooth * (this.density.length - 1))
    return this.density[Math.min(idx, this.density.length - 1)]
  }

  /**
   * Render buffer to canvas
   */
  renderToCanvas(
    ctx: CanvasRenderingContext2D,
    charWidth: number,
    charHeight: number,
    color = '#00ff88'
  ): void {
    ctx.font = `${charHeight}px monospace`
    ctx.fillStyle = color
    ctx.textBaseline = 'top'

    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const idx = col + row * this.cols
        const cell = this.cells[idx]

        if (cell.value > 0.05) {
          // Skip very dim cells
          const char = cell.char || this.getChar(cell.value)
          ctx.globalAlpha = Math.min(1, cell.value)
          ctx.fillText(char, col * charWidth, row * charHeight)
        }
      }
    }
    ctx.globalAlpha = 1.0
  }
}
